import React, { useEffect, useState, useCallback } from 'react';
import { AdminPageHead } from '../components/AdminPageHead';
import {
    Box, Card, CardContent, Typography, TextField, Button, Chip,
    Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
    TablePagination, CircularProgress, Dialog, DialogTitle, DialogContent,
    DialogActions, Autocomplete, Tooltip, Divider, RadioGroup,
    FormControlLabel, Radio, FormLabel, FormControl, IconButton, Avatar,
} from '@mui/material';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import PersonOutlinedIcon from '@mui/icons-material/PersonOutlined';
import BlockOutlinedIcon from '@mui/icons-material/BlockOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import { connect } from 'react-redux';
import { AdminDashboardService } from '../../../../../core/services/admin-dashboard.service';
import { formatEntitlementLimit } from '../../../../../core/utility';

const adminService = new AdminDashboardService();

interface Props {
    userData: any;
}

interface InfluencerOption {
    influencer_id: number;
    name: string | null;
    primary_email: string | null;
    primary_phone: string | null;
    instagram_username: string | null;
    instagram_display_name: string | null;
    automation_count: number;
}

interface PrivilegeRow {
    ig_foc_zap_privilege_id: number;
    influencer_id: number;
    influencer_name: string | null;
    influencer_email: string | null;
    influencer_phone: string | null;
    instagram_username: string | null;
    plan_type: 'Monthly' | 'Annual Plan';
    remark: string | null;
    granted_by_name: string | null;
    is_active: number;
    created_at: string;
    updated_at: string;
}

const PLAN_TYPE_LABELS: Record<string, string> = {
    'Monthly': 'Monthly Limit',
    'Annual Plan': 'Annual Limit',
};

const PLAN_TYPE_COLOR: Record<string, 'primary' | 'secondary'> = {
    'Monthly': 'primary',
    'Annual Plan': 'secondary',
};

// Maps ig_foc_zap_privilege.plan_type → planLimits key returned by backend
const PLAN_LIMITS_KEY: Record<string, 'monthly' | 'yearly'> = {
    'Monthly': 'monthly',
    'Annual Plan': 'yearly',
};

const fmtDate = (v: string | null) =>
    v ? new Date(v).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

const FocZapPrivilegeManager: React.FC<Props> = ({ userData }) => {
    // --- search ---
    const [searchInput, setSearchInput] = useState('');
    const [searchOptions, setSearchOptions] = useState<InfluencerOption[]>([]);
    const [searching, setSearching] = useState(false);
    const [selectedInfluencer, setSelectedInfluencer] = useState<InfluencerOption | null>(null);

    // --- grant dialog ---
    const [dialogOpen, setDialogOpen] = useState(false);
    const [planType, setPlanType] = useState<'Monthly' | 'Annual Plan'>('Monthly');
    const [remark, setRemark] = useState('');
    const [granting, setGranting] = useState(false);
    const [grantError, setGrantError] = useState('');

    // --- plan limits (fetched once on mount) ---
    const [planLimits, setPlanLimits] = useState<{ monthly: Record<string, any> | null; yearly: Record<string, any> | null }>({ monthly: null, yearly: null });
    const [limitsLoading, setLimitsLoading] = useState(false);

    // --- privileges list ---
    const [privileges, setPrivileges] = useState<PrivilegeRow[]>([]);
    const [listLoading, setListLoading] = useState(false);
    const [listError, setListError] = useState('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    // --- revoke confirm ---
    const [revokeTarget, setRevokeTarget] = useState<PrivilegeRow | null>(null);
    const [revoking, setRevoking] = useState(false);

    // Debounce search
    useEffect(() => {
        const q = searchInput.trim();
        if (q.length < 2) { setSearchOptions([]); return; }
        let cancelled = false;
        setSearching(true);
        const t = setTimeout(async () => {
            try {
                const res = await adminService.searchFocInfluencers(q);
                if (!cancelled) setSearchOptions(res?.data?.rows?.influencers || []);
            } catch {
                if (!cancelled) setSearchOptions([]);
            } finally {
                if (!cancelled) setSearching(false);
            }
        }, 300);
        return () => { cancelled = true; clearTimeout(t); };
    }, [searchInput]);

    const loadPrivileges = useCallback(async () => {
        setListLoading(true);
        setListError('');
        try {
            const res = await adminService.getFocZapPrivileges();
            const data = res?.data?.rows?.privileges ?? res?.data?.rows;
            setPrivileges(Array.isArray(data) ? data : []);
        } catch {
            setListError('Failed to load privileges.');
        } finally {
            setListLoading(false);
        }
    }, []);

    useEffect(() => {
        loadPrivileges();
        // Fetch plan limits once
        setLimitsLoading(true);
        adminService.getZapPlanLimits()
            .then((res) => setPlanLimits(res?.data?.rows || { monthly: null, yearly: null }))
            .catch(() => {/* silent — limits just won't display */})
            .finally(() => setLimitsLoading(false));
    }, [loadPrivileges]);

    const openGrantDialog = () => {
        if (!selectedInfluencer) return;
        setPlanType('Monthly');
        setRemark('');
        setGrantError('');
        setDialogOpen(true);
    };

    const handleGrant = async () => {
        if (!selectedInfluencer) return;
        if (!remark.trim()) { setGrantError('Remark is required.'); return; }
        setGranting(true);
        setGrantError('');
        try {
            const res: any = await adminService.grantFocZapPrivilege({
                influencer_id: selectedInfluencer.influencer_id,
                plan_type: planType,
                remark: remark.trim(),
                granted_by: userData?.user_id,
            });
            // Surface backend failures instead of silently closing the dialog.
            if (res?.status === 1) {
                setDialogOpen(false);
                setSelectedInfluencer(null);
                setSearchInput('');
                loadPrivileges();
            } else {
                setGrantError(res?.info || 'Failed to grant privilege. Please try again.');
            }
        } catch (e: any) {
            setGrantError(e?.message || 'Failed to grant privilege. Please try again.');
        } finally {
            setGranting(false);
        }
    };

    const handleRevoke = async () => {
        if (!revokeTarget) return;
        setRevoking(true);
        try {
            await adminService.revokeFocZapPrivilege(revokeTarget.ig_foc_zap_privilege_id);
            setRevokeTarget(null);
            loadPrivileges();
        } catch {
            /* silent — list will stay stale; user can refresh */
        } finally {
            setRevoking(false);
        }
    };

    return (
        <Box sx={{ width: '100%', boxSizing: 'border-box', overflowX: 'hidden' }}>

            <AdminPageHead
                title="FOC Zap Privilege"
                subtitle="Give chosen FOC (free of cost) creators the Zap limits of a Monthly or Yearly subscriber. Their entitlements update automatically."
                actions={
                    <Tooltip title="Refresh list">
                        <IconButton onClick={loadPrivileges} size="small" disabled={listLoading}>
                            <RefreshIcon fontSize="small" />
                        </IconButton>
                    </Tooltip>
                }
            />

            {/* Grant section */}
            <Card variant="outlined" sx={{ mb: 3, background: '#fafdf6' }}>
                <CardContent>
                    <Typography variant="subtitle2" fontWeight={600} mb={1}>
                        Grant Zap Privilege to a FOC Influencer
                    </Typography>
                    <Typography variant="caption" color="text.secondary" display="block" mb={2}>
                        Search by name, email, phone, or Instagram username. Select the influencer, choose the plan type, add a remark, then click Grant.
                    </Typography>

                    <Box display="flex" alignItems="flex-start" gap={2} flexWrap="wrap">
                        <Autocomplete<InfluencerOption, false, false, false>
                            size="small"
                            sx={{ width: 380, background: '#fff' }}
                            value={selectedInfluencer}
                            onChange={(_, v) => setSelectedInfluencer(v)}
                            inputValue={searchInput}
                            onInputChange={(_, v) => setSearchInput(v)}
                            options={searchOptions}
                            loading={searching}
                            filterOptions={(x) => x}
                            isOptionEqualToValue={(o, v) => o.influencer_id === v.influencer_id}
                            getOptionLabel={(o) => {
                                if (!o) return '';
                                const handle = o.instagram_username ? `@${o.instagram_username}` : '';
                                const nm = o.name || o.instagram_display_name || '';
                                return [handle, nm].filter(Boolean).join(' · ') || `#${o.influencer_id}`;
                            }}
                            renderOption={(props, o) => (
                                <li {...props} key={o.influencer_id}>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <PersonOutlinedIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                                            <Typography variant="body2" fontWeight={500}>
                                                {o.instagram_username ? `@${o.instagram_username}` : (o.name || `#${o.influencer_id}`)}
                                            </Typography>
                                            <Chip label={`#${o.influencer_id}`} size="small" variant="outlined" sx={{ height: 18, fontSize: 10, fontFamily: 'monospace' }} />
                                        </Box>
                                        <Typography variant="caption" color="text.secondary" noWrap>
                                            {[o.name, o.primary_email, o.primary_phone].filter(Boolean).join(' · ') || '—'}
                                        </Typography>
                                    </Box>
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label="Search Influencer"
                                    placeholder="Name, email, phone, or @handle"
                                    InputProps={{
                                        ...params.InputProps,
                                        endAdornment: (
                                            <>
                                                {searching ? <CircularProgress size={16} /> : null}
                                                {params.InputProps.endAdornment}
                                            </>
                                        ),
                                    }}
                                />
                            )}
                            noOptionsText={searchInput.trim().length < 2 ? 'Type at least 2 characters' : 'No matches'}
                            clearOnEscape
                        />

                        <Button
                            variant="contained"
                            size="small"
                            startIcon={<BoltOutlinedIcon />}
                            disabled={!selectedInfluencer}
                            onClick={openGrantDialog}
                            sx={{ mt: 0.5 }}
                        >
                            Grant Privilege
                        </Button>
                    </Box>

                    {selectedInfluencer && (
                        <Box mt={1.5} display="flex" alignItems="center" gap={1} flexWrap="wrap">
                            <Avatar sx={{ width: 28, height: 28, bgcolor: '#f59e0b', fontSize: 13 }}>
                                {(selectedInfluencer.name || selectedInfluencer.instagram_username || '?')[0].toUpperCase()}
                            </Avatar>
                            <Typography variant="body2" fontWeight={500}>
                                {selectedInfluencer.name || selectedInfluencer.instagram_username || `#${selectedInfluencer.influencer_id}`}
                            </Typography>
                            {selectedInfluencer.instagram_username && (
                                <Chip label={`@${selectedInfluencer.instagram_username}`} size="small" variant="outlined" />
                            )}
                            {selectedInfluencer.primary_email && (
                                <Typography variant="caption" color="text.secondary">{selectedInfluencer.primary_email}</Typography>
                            )}
                            {selectedInfluencer.primary_phone && (
                                <Typography variant="caption" color="text.secondary">{selectedInfluencer.primary_phone}</Typography>
                            )}
                        </Box>
                    )}
                </CardContent>
            </Card>

            <Divider sx={{ mb: 3 }} />

            {/* Existing privileges table */}
            <Typography variant="subtitle2" fontWeight={600} mb={1}>
                Granted Privileges
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
                All FOC influencers who have been granted elevated Zap plan limits.
            </Typography>

            {listError && <Typography color="error" variant="body2" mb={1}>{listError}</Typography>}

            <TableContainer sx={{ border: '1px solid #e0e0e0', borderRadius: 1, overflowX: 'auto' }}>
                <Table size="small" sx={{ minWidth: 700 }}>
                    <TableHead>
                        <TableRow sx={{ background: '#f5f5f5' }}>
                            <TableCell><strong>Influencer</strong></TableCell>
                            <TableCell><strong>Zap Limit</strong></TableCell>
                            <TableCell><strong>Remark</strong></TableCell>
                            <TableCell><strong>Granted By</strong></TableCell>
                            <TableCell><strong>Date</strong></TableCell>
                            <TableCell><strong>Status</strong></TableCell>
                            <TableCell align="center"><strong>Action</strong></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {listLoading ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                                    <CircularProgress size={24} />
                                </TableCell>
                            </TableRow>
                        ) : privileges.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                                    No privileges granted yet.
                                </TableCell>
                            </TableRow>
                        ) : privileges
                            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                            .map((row) => (
                                <TableRow key={row.ig_foc_zap_privilege_id} hover>
                                    <TableCell>
                                        <Box display="flex" flexDirection="column">
                                            <Typography variant="body2" fontWeight={500}>
                                                {row.influencer_name || `#${row.influencer_id}`}
                                            </Typography>
                                            {row.instagram_username && (
                                                <Typography variant="caption" color="text.secondary">
                                                    @{row.instagram_username}
                                                </Typography>
                                            )}
                                            {row.influencer_email && (
                                                <Typography variant="caption" color="text.secondary">
                                                    {row.influencer_email}
                                                </Typography>
                                            )}
                                        </Box>
                                    </TableCell>
                                    <TableCell>
                                        <Chip
                                            label={PLAN_TYPE_LABELS[row.plan_type] || row.plan_type}
                                            size="small"
                                            color={PLAN_TYPE_COLOR[row.plan_type] || 'default'}
                                            variant="outlined"
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ maxWidth: 200, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                            {row.remark || '—'}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{row.granted_by_name || '—'}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="caption" color="text.secondary">
                                            {fmtDate(row.created_at)}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        {row.is_active ? (
                                            <Chip label="Active" size="small" color="success" icon={<CheckCircleOutlineIcon />} />
                                        ) : (
                                            <Chip label="Revoked" size="small" color="default" />
                                        )}
                                    </TableCell>
                                    <TableCell align="center">
                                        {row.is_active ? (
                                            <Tooltip title="Revoke privilege — user will revert to FOC entitlements">
                                                <Button
                                                    size="small"
                                                    color="error"
                                                    variant="outlined"
                                                    startIcon={<BlockOutlinedIcon />}
                                                    onClick={() => setRevokeTarget(row)}
                                                >
                                                    Revoke
                                                </Button>
                                            </Tooltip>
                                        ) : (
                                            <Typography variant="caption" color="text.disabled">Revoked</Typography>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                    </TableBody>
                </Table>
                {privileges.length > 0 && (
                    <TablePagination
                        component="div"
                        count={privileges.length}
                        page={page}
                        onPageChange={(_, p) => setPage(p)}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
                        rowsPerPageOptions={[5, 10, 25]}
                    />
                )}
            </TableContainer>

            {/* Grant Dialog */}
            <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BoltOutlinedIcon sx={{ color: '#f59e0b' }} />
                    Grant Zap Privilege
                </DialogTitle>
                <DialogContent dividers>
                    {selectedInfluencer && (
                        <Card variant="outlined" sx={{ mb: 2, background: '#fafafa' }}>
                            <CardContent sx={{ py: '10px !important' }}>
                                <Typography variant="caption" color="text.secondary" display="block" mb={0.5}>
                                    Selected Influencer
                                </Typography>
                                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                                    <Avatar sx={{ width: 32, height: 32, bgcolor: '#f59e0b', fontSize: 14 }}>
                                        {(selectedInfluencer.name || selectedInfluencer.instagram_username || '?')[0].toUpperCase()}
                                    </Avatar>
                                    <Box>
                                        <Typography variant="body2" fontWeight={600}>
                                            {selectedInfluencer.name || selectedInfluencer.instagram_username || `#${selectedInfluencer.influencer_id}`}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            {[
                                                selectedInfluencer.instagram_username ? `@${selectedInfluencer.instagram_username}` : null,
                                                selectedInfluencer.primary_email,
                                                selectedInfluencer.primary_phone,
                                            ].filter(Boolean).join(' · ') || `ID: ${selectedInfluencer.influencer_id}`}
                                        </Typography>
                                    </Box>
                                </Box>
                            </CardContent>
                        </Card>
                    )}

                    <FormControl component="fieldset" sx={{ mb: 2, width: '100%' }}>
                        <FormLabel component="legend" sx={{ fontWeight: 600, fontSize: 14, mb: 0.5 }}>
                            Plan Type to Apply
                        </FormLabel>
                        <Typography variant="caption" color="text.secondary" display="block" mb={1.5}>
                            The FOC user will receive the Zap feature limits of the selected plan type.
                        </Typography>
                        <RadioGroup
                            row
                            value={planType}
                            onChange={(e) => setPlanType(e.target.value as 'Monthly' | 'Annual Plan')}
                            sx={{ gap: 2, alignItems: 'flex-start' }}
                        >
                            {(['Monthly', 'Annual Plan'] as const).map((type) => {
                                const limits = planLimits[PLAN_LIMITS_KEY[type]];
                                const isSelected = planType === type;
                                return (
                                    <FormControlLabel
                                        key={type}
                                        value={type}
                                        control={<Radio size="small" sx={{ mt: 0.3, alignSelf: 'flex-start' }} />}
                                        sx={{ alignItems: 'flex-start', m: 0, flex: 1 }}
                                        label={
                                            <Box
                                                sx={{
                                                    border: '1px solid',
                                                    borderColor: isSelected ? 'primary.main' : 'divider',
                                                    borderRadius: 1.5,
                                                    p: 1.2,
                                                    background: isSelected ? '#f0f7ff' : '#fafafa',
                                                    minWidth: 180,
                                                    transition: 'all 0.15s',
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                <Typography variant="body2" fontWeight={600} mb={0.3}>
                                                    {type}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary" display="block" mb={0.8}>
                                                    Apply {type} subscription Zap limits
                                                </Typography>
                                                {limitsLoading ? (
                                                    <CircularProgress size={12} />
                                                ) : limits ? (
                                                    <Box component="ul" sx={{ m: 0, p: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 0.4 }}>
                                                        {[
                                                            { key: 'daily_interaction_limit',    label: 'Daily Interactions' },
                                                            { key: 'max_active_automation_posts', label: 'Max Active Posts' },
                                                            { key: 'max_comment_rules',           label: 'Max Comment Rules' },
                                                            { key: 'max_dm_rules',                label: 'Max DM Rules' },
                                                            { key: 'max_keywords_per_rule',       label: 'Keywords / Rule' },
                                                            { key: 'zap_link_limit',              label: 'Zap Link Limit' },
                                                            { key: 'storefront_enabled',          label: 'Zap Storefront' },
                                                            { key: 'link_in_bio_enabled',         label: 'Zap Link in Bio' },
                                                        ].filter(({ key }) => limits[key] !== undefined && limits[key] !== null)
                                                         .map(({ key, label }) => (
                                                            <Box component="li" key={key} display="flex" justifyContent="space-between" alignItems="center" gap={1}>
                                                                <Typography variant="caption" color="text.secondary">{label}</Typography>
                                                                <Chip
                                                                    // OLD (pre-unlimited): label={... : limits[key]} — a -1 rendered as "-1".
                                                                    label={typeof limits[key] === 'boolean' ? (limits[key] ? 'Included' : 'No') : formatEntitlementLimit(limits[key])}
                                                                    size="small"
                                                                    sx={{ height: 18, fontSize: 11, fontWeight: 600, fontFamily: 'monospace' }}
                                                                    color={isSelected ? 'primary' : 'default'}
                                                                    variant={isSelected ? 'filled' : 'outlined'}
                                                                />
                                                            </Box>
                                                        ))}
                                                    </Box>
                                                ) : (
                                                    <Typography variant="caption" color="text.disabled" fontStyle="italic">
                                                        Limits not available
                                                    </Typography>
                                                )}
                                            </Box>
                                        }
                                    />
                                );
                            })}
                        </RadioGroup>
                    </FormControl>

                    <TextField
                        label="Remark *"
                        placeholder="e.g. Special access for beta testing, Trial user for Q3 campaign..."
                        fullWidth
                        multiline
                        rows={3}
                        size="small"
                        value={remark}
                        onChange={(e) => { setRemark(e.target.value); setGrantError(''); }}
                        error={!!grantError && !remark.trim()}
                        helperText={!remark.trim() && grantError ? grantError : ''}
                    />

                    {grantError && remark.trim() && (
                        <Typography color="error" variant="caption" display="block" mt={1}>{grantError}</Typography>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, py: 2 }}>
                    <Button onClick={() => setDialogOpen(false)} disabled={granting}>Cancel</Button>
                    <Button
                        variant="contained"
                        onClick={handleGrant}
                        disabled={granting || !remark.trim()}
                        startIcon={granting ? <CircularProgress size={16} /> : <BoltOutlinedIcon />}
                    >
                        {granting ? 'Granting...' : 'Grant Privilege'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Revoke Confirm Dialog */}
            <Dialog open={!!revokeTarget} onClose={() => setRevokeTarget(null)} maxWidth="xs" fullWidth>
                <DialogTitle>Revoke Privilege?</DialogTitle>
                <DialogContent>
                    <Typography variant="body2">
                        This will revoke the <strong>{revokeTarget?.plan_type}</strong> Zap privilege for{' '}
                        <strong>{revokeTarget?.influencer_name || `#${revokeTarget?.influencer_id}`}</strong>.
                        Their entitlements will revert to the default FOC plan limits.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRevokeTarget(null)} disabled={revoking}>Cancel</Button>
                    <Button
                        color="error"
                        variant="contained"
                        onClick={handleRevoke}
                        disabled={revoking}
                        startIcon={revoking ? <CircularProgress size={16} /> : <BlockOutlinedIcon />}
                    >
                        {revoking ? 'Revoking...' : 'Revoke'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

const mapStateToProps = (state: any) => ({
    userData: state.UserReducer.UserData,
});

const connectToRedux = connect(mapStateToProps)(FocZapPrivilegeManager);
export { connectToRedux as FocZapPrivilegeManager };
